'use client'

import { useContext } from 'react'
import { ThemeContext } from '@/src/contexts'
import { trainingGuideSteps } from '@/src/data/content/trainingGuideSteps'

import { Typography, Steps } from 'antd'
import { Accordion, Button } from '@/src/components'

import Image from 'next/image'
import { initIntercom } from '@/src/lib'

interface HeroProps {
  subtitle: string
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  section: string
  onStartTraining?: () => void
}

const { Text, Title } = Typography

export const Hero = ({
  subtitle,
  title,
  description,
  imageSrc,
  imageAlt,
  section,
  onStartTraining,
}: HeroProps) => {
  const { isDarkMode } = useContext(ThemeContext)

  const items = [
    ...trainingGuideSteps.slice(0, 5).map((step) => ({
      title: step.contentTitle,
      icon: step.contentIcon,
    })),
    {
      title: 'And more!',
      icon: '/assets/training-guide/illustrations/bubble-speech.png',
    },
  ]

  return (
    <div className='max-w-screen-lg px-4 mx-auto'>
      <div className='flex flex-col md:flex-row justify-between items-stretch h-full'>
        <div className='flex flex-col md:w-1/2 lg:w-1/2'>
          <div className='flex flex-col justify-start items-start text-start lg:max-w-[600px]'>
            <Title
              level={5}
              style={{
                color: '#57ADB9',
                marginBottom: 0,
                fontWeight: 400,
              }}
            >
              {subtitle}
            </Title>
            <Title
              level={3}
              style={{
                color: `${isDarkMode ? '#38A7C8' : '#274765'}`,
                margin: 0,
                marginBottom: 8,
                display: 'inline',
                fontWeight: 400,
                minHeight: '60px',
              }}
            >
              {title}
            </Title>
            <Text
              type='secondary'
              style={{
                fontSize: 18,
                fontWeight: 300,
                marginBottom: 32,
                minHeight: '80px',
                display: 'block',
              }}
              className='max-w-2xl'
            >
              {description}
            </Text>
            {section === 'training-guide' && (
              <div className='mt-auto w-full mb-8 ml-0 md:ml-8 '>
                <Steps
                  direction='vertical'
                  current={-1}
                  items={items.map((item) => ({
                    title: item.title,
                    icon: (
                      <img
                        src={item.icon}
                        alt='step icon'
                        style={{ width: 30, height: 30 }}
                      />
                    ),
                  }))}
                />
              </div>
            )}
          </div>
          {section === 'faq' && (
            <div className='items-end block mt-auto'>
              <Accordion accordionType='mainAccordion' />
            </div>
          )}
          {section === 'training-guide' && (
            <Button
              type='primary'
              size='large'
              className='w-full'
              onClick={onStartTraining}
              text='Start Training'
            />
          )}
          {section === 'training-guide-complete' && (
            <div className='flex flex-row justify-between gap-4'>
              <Button
                type='primary'
                size='large'
                className='w-36'
                href='/home'
                text='Back to Home'
              />
              <Button
                type='primary'
                size='large'
                className='w-36'
                href='/search'
                text='Find a Physician'
              />
            </div>
          )}
          {section === 'contact-us' && (
            <Button
              type='primary'
              size='large'
              className='w-36'
              onClick={async () => {
                await initIntercom()
                if (window.Intercom) {
                  window.Intercom('show')
                }
              }}
              text='Chat with Us'
            />
          )}
        </div>
        <div className='hidden md:flex md:w-1/2 lg:w-1/2 items-start justify-end'>
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={320}
            height={320}
            className='hidden md:block h-auto w-full max-w-[320px] lg:max-w-[320px] object-contain'
            priority
            loading='eager'
          />
        </div>
      </div>
    </div>
  )
}
